import { useState, useCallback, useEffect } from "react";
import { Box, VisuallyHidden } from "@chakra-ui/react";
import { useBreakpointValue } from "@chakra-ui/react";
import { Drawer } from "vaul";

type VaulDrawerProps = {
  drawerContainerRef?: HTMLDivElement | null;
  setDrawerHeight: (height: number) => void;
  drawerHeight: number;
  snapPoints: number[];
  children?: React.ReactNode;
};

const MobileBottomSheet = ({
  children,
  drawerContainerRef,
  drawerHeight,
  setDrawerHeight,
  snapPoints,
}: VaulDrawerProps) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const isMobile = useBreakpointValue({ base: true, md: false });

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      if (!isDragging) return;
      const newHeight = Math.max(
        snapPoints[0],
        Math.min(1, 1 - event.clientY / window.innerHeight)
      );
      setDrawerHeight(newHeight);
    },
    [isDragging, setDrawerHeight, snapPoints]
  );
  
  const handlePointerUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    const closest = snapPoints.reduce(
      (prev, curr) =>
        Math.abs(curr - drawerHeight) < Math.abs(prev - drawerHeight) ? curr : prev,
      snapPoints[1]
    );
    setDrawerHeight(closest);
  }, [isDragging, setDrawerHeight, drawerHeight, snapPoints]);

  const handlePointerDown = (event: React.PointerEvent) => {
    setIsDragging(true);
    event.preventDefault();
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
    } else {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    }

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [isDragging, handlePointerMove, handlePointerUp]);

  if (!isMobile) {
    return (
      <Box
        mt="0"
        zIndex="0"
        p="3"
        shadow="base"
        bg="white"
        bottom="initial"
        overflow="scroll"
        borderLeftWidth="1px"
        borderColor="border.muted"
      >
        {children}
      </Box>
    );
  }
  return (
    <Drawer.Root
      open={true}
      dismissible={false}
      defaultOpen
      modal={false}
      snapToSequentialPoint
      container={drawerContainerRef}
    >
      <Box
        as={Drawer.Content}
        position="absolute"
        rounded="md"
        zIndex="1000"
        p="3"
        shadow="0px -4px 4px color-mix(in srgb, var(--chakra-colors-gray-900) 10%, transparent),0px -1px 1px color-mix(in srgb, var(--chakra-colors-gray-900) 30%, transparent)"
        bg="white"
        bottom={drawerHeight === snapPoints[2] ? 0 : 2}
        left="0"
        right="0"
        overflow="scroll"
        height={{ base: `${drawerHeight * 100}%`, sm: `calc(${drawerHeight * 100}% - 2.25rem)`}} // subtracting the height of the inpage navigation on non-mobile sizes
        transition="height 0.2s ease-in-out"
      >
        <Box
          w="100%"
          mt="2"
          bg="transparent"
          cursor="pointer"
          as={Drawer.Handle}
          onPointerDown={handlePointerDown}
        >
          <Box bg="gray.200" pos="relative" borderRadius="full" top="2" h="1" w="12" mx="auto" />
        </Box>
        <VisuallyHidden asChild>
          <Drawer.Title>Observation form</Drawer.Title>
        </VisuallyHidden>
        <VisuallyHidden asChild>
          <Drawer.Description>Observation form</Drawer.Description>
        </VisuallyHidden>
        {children}
      </Box>
    </Drawer.Root>
  );
};

export default MobileBottomSheet;
